<template>
  <div class="employee-page">
    <HeaderBar />

    <main class="content">
      <el-card class="step-card" shadow="hover">
        <div class="step-copy">
          <p class="step-label">Step 2 of 3</p>
          <h1>Complete the employee form</h1>
          <p class="step-text">
            Open the required employee form in a new tab, complete it there, then confirm below.
          </p>
        </div>

        <el-alert
          class="status-alert"
          type="info"
          :closable="false"
          title="Finish the external form before moving to approval review."
        />

        <div class="panel">
          <p class="panel-title">Required employee form</p>
          <p class="panel-text">
            This step sends you to the external employee form configured for the portal.
          </p>

          <div class="actions">
            <el-button
              type="primary"
              size="large"
              :disabled="!formUrl"
              @click="openEmployeeForm"
            >
              Open employee form
            </el-button>
            <el-button
              type="success"
              size="large"
              :loading="submitting"
              @click="confirmFormCompleted"
            >
              I have completed the form
            </el-button>
            <el-button size="large" @click="logout">
              Log out
            </el-button>
          </div>

          <p v-if="!formUrl" class="panel-note">
            Set <code>VUE_APP_EMPLOYEE_FORM_URL</code> to enable the external employee form link.
          </p>
        </div>
      </el-card>
    </main>
  </div>
</template>

<script>
import HeaderBar from '@/components/HeaderBar.vue'
import { auth, serverTimestamp } from '@/firebase'
import {
  EMPLOYEE_PORTAL_STATUSES,
  getEmployeeFormUrl,
  mergeUserProfile,
} from '@/services/userProfile'
import { ElMessage } from 'element-plus'
import { signOut } from 'firebase/auth'

export default {
  name: 'EmployeeFormStepView',
  components: { HeaderBar },
  data() {
    return {
      formUrl: getEmployeeFormUrl(),
      submitting: false,
    }
  },
  methods: {
    openEmployeeForm() {
      if (!this.formUrl) {
        ElMessage({ type: 'warning', message: 'Employee form URL is not configured.', showClose: true })
        return
      }
      window.open(this.formUrl, '_blank', 'noreferrer')
    },
    async confirmFormCompleted() {
      if (this.submitting) return

      const user = auth.currentUser
      if (!user) {
        this.$router.push({ name: 'Login' })
        return
      }

      this.submitting = true
      try {
        await mergeUserProfile(user, {
          employeePortal: {
            status: EMPLOYEE_PORTAL_STATUSES.PENDING_APPROVAL,
            formCompletedAt: serverTimestamp(),
            approvedAt: null,
            approvedBy: '',
            approvedByUid: '',
            approvedByEmail: '',
            denialComment: '',
            deniedAt: null,
            deniedBy: '',
            deniedByUid: '',
            deniedByEmail: '',
          }
        })

        ElMessage({ type: 'success', message: 'Your employee onboarding was submitted for review.', showClose: true })
        this.$router.push({ name: 'EmployeePendingApproval' })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to update your onboarding status right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.submitting = false
      }
    },
    async logout() {
      try {
        await signOut(auth)
      } catch (e) {}
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>

<style scoped>
.employee-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f6fbff 0%, #e9f2ff 100%);
  color: #03241f;
}

.content {
  max-width: 840px;
  margin: 36px auto 64px;
  padding: 0 16px;
  box-sizing: border-box;
}

.step-card {
  border-radius: 22px;
  border: 1px solid rgba(3, 36, 31, 0.1);
  box-shadow: 0 20px 48px rgba(3, 36, 31, 0.12);
}

.step-copy {
  margin-bottom: 20px;
}

.step-label {
  margin: 0 0 8px;
  color: #0b6cff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.step-copy h1 {
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 800;
}

.step-text {
  margin: 0;
  color: #486a64;
  font-size: 1rem;
}

.status-alert {
  margin-bottom: 24px;
}

.panel {
  border: 1px solid rgba(3, 36, 31, 0.12);
  border-radius: 18px;
  padding: 22px;
  background: #f9fcff;
}

.panel-title {
  margin: 0 0 8px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #03241f;
}

.panel-text {
  margin: 0 0 18px;
  color: #486a64;
}

.panel-note {
  margin: 16px 0 0;
  color: #8b5e00;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

@media (max-width: 600px) {
  .content {
    margin-top: 28px;
  }

  .step-copy h1 {
    font-size: 1.7rem;
  }

  .actions {
    flex-direction: column;
  }
}
</style>
